import { type CSSProperties, type RefObject, useRef, useState } from "react";

import { QUESTIONS, type Stats, percentage } from "../../shared";
import { profileFor } from "../profile";
import type { Progress } from "../survey";
import { AnimatedNumber } from "../ui";
import { PAPERNAUT_URL, WEBINAR } from "../webinar";
import { Confetti } from "./Confetti";

/** Where each sheet starts in the chaos, and how it lies once it has settled on the stack. */
const SHEETS = [
	{ x: "-150px", y: "-70px", from: "-26deg", to: "-5deg" },
	{ x: "160px", y: "-100px", from: "34deg", to: "4deg" },
	{ x: "-130px", y: "120px", from: "18deg", to: "-2deg" },
	{ x: "140px", y: "130px", from: "-38deg", to: "3deg" },
];

function Celebration({ stampRef }: { stampRef: RefObject<HTMLSpanElement | null> }) {
	return (
		<div
			className="celebration"
			role="img"
			aria-label="Verstreute Blätter ordnen sich zu einem Stapel, darauf landet ein Stempel „Danke!“."
		>
			<div className="celebration-stack absolute inset-0">
				{SHEETS.map((sheet, i) => (
					<span
						key={sheet.from}
						className="celebration-sheet"
						style={
							{
								"--from-x": sheet.x,
								"--from-y": sheet.y,
								"--from-r": sheet.from,
								"--to-r": sheet.to,
								"--to-y": `${(SHEETS.length - i) * 3}px`,
								"--delay": `${i * 110}ms`,
							} as CSSProperties
						}
					/>
				))}
				<div className="doc-sheet celebration-top">
					<p className="text-left text-base font-bold leading-tight">Ihre Antworten</p>
					<p className="mt-0.5 text-left text-xs text-muted-foreground">{WEBINAR.date}</p>
					<div className="mt-5 grid gap-2">
						<div className="doc-line h-1.5 w-4/5" />
						<div className="doc-line h-1.5" />
						<div className="doc-line h-1.5 w-3/5" />
						<div className="doc-line h-1.5" />
						<div className="doc-line h-1.5 w-2/3" />
						<div className="doc-line h-1.5 w-1/2" />
					</div>
					<span ref={stampRef} className="doc-stamp celebration-stamp">
						Danke!
					</span>
				</div>
			</div>
			<p className="note celebration-note">Mission erfüllt ✓</p>
			<img src="/dist/brand/papernaut_icon.svg" alt="" className="celebration-astronaut" />
		</div>
	);
}

type Props = {
	progress: Progress;
	stats: Stats;
	onEdit: () => void;
};

export function Finish({ progress, stats, onEdit }: Props) {
	const stampRef = useRef<HTMLSpanElement>(null);
	const [round, setRound] = useState(0);
	const profile = profileFor(progress.answers);

	const comparisons = QUESTIONS.flatMap((question) => {
		const optionId = progress.answers[question.id]?.[0];
		const option = question.options.find((candidate) => candidate.id === optionId);
		return question.compare && option
			? [{ question, option, share: percentage(stats.questions[question.id], option.id) }]
			: [];
	});

	return (
		<div className="grid gap-6">
			<section className="deck starfield rounded-[1.75rem] px-7 pt-14 pb-10 text-center sm:px-10 md:rounded-[2.25rem]">
				<Celebration key={round} stampRef={stampRef} />
				<Confetti key={`confetti-${round}`} origin={stampRef} delay={1500} />

				<div className="celebration-copy mt-14">
					<span className="deck-label">Sicher gelandet</span>
					<h1 className="display-title mt-5 text-white">
						Danke fürs Mitmachen{progress.firstName ? `, ${progress.firstName}` : ""}!
					</h1>
					<p className="mx-auto mt-4 max-w-md text-lg leading-relaxed text-ice">
						{progress.rank ? `Sie sind als ${progress.rank}. Person gelandet. ` : "Ihre Antworten sind gelandet. "}
						Die Ergebnisse schauen wir uns gleich gemeinsam im Webinar an.
					</p>
					<button type="button" className="btn-deck mt-6" onClick={() => setRound((value) => value + 1)}>
						Nochmal feiern <span aria-hidden="true">🎉</span>
					</button>
				</div>
			</section>

			<section className="surface-card screen-on p-6 sm:p-8">
				<span className="eyebrow">Ihr Ablage-Typ</span>
				<div className="mt-5 flex items-start gap-5">
					<img src="/dist/brand/papernaut_icon.svg" alt="" className="drift w-16 shrink-0 -rotate-6 sm:w-20" />
					<div>
						<h2 className="text-2xl font-semibold">{profile.title}</h2>
						<p className="mt-2 leading-relaxed text-muted-foreground">{profile.text}</p>
					</div>
				</div>
			</section>

			{comparisons.length > 0 && (
				<section className="surface-card p-6 sm:p-8">
					<div className="flex items-center gap-2">
						<span className="live-dot" aria-hidden="true" />
						<h2 className="text-xl font-semibold">Sie sind nicht allein</h2>
					</div>
					<p className="mt-1 text-sm text-muted-foreground">
						Live aus <AnimatedNumber value={stats.participants} /> Teilnahmen – die Zahlen bewegen sich,
						während andere noch abstimmen.
					</p>
					<ul className="mt-6 grid gap-px overflow-hidden rounded-xl border border-border bg-border sm:grid-cols-2">
						{comparisons.map(({ question, option, share }) => (
							<li key={question.id} className="bg-card p-5">
								<p className="text-3xl font-bold text-primary">
									<AnimatedNumber value={share} /> %
								</p>
								<p className="mt-1 text-sm leading-relaxed text-muted-foreground">
									{question.compare}:{" "}
									<span className="font-medium text-foreground">
										{option.emoji} {option.label}
									</span>
								</p>
							</li>
						))}
					</ul>
				</section>
			)}

			<section className="surface-card p-6 sm:p-8">
				<h2 className="text-xl font-semibold">Und nach dem Webinar?</h2>
				<p className="mt-2 leading-relaxed text-muted-foreground">
					Papernaut liest Dokumente mit KI, ordnet sie automatisch ein und macht ihren Zusammenhang
					durchsuchbar – mit Firmensitz, Servern und KI in der EU.
				</p>
				<div className="mt-6 flex flex-wrap gap-3">
					<a className="btn-launch" href={PAPERNAUT_URL} target="_blank" rel="noopener">
						Papernaut kostenlos testen
					</a>
					<a className="btn-secondary" href="/live">
						Alle Live-Ergebnisse
					</a>
					<button type="button" className="btn-secondary" onClick={onEdit}>
						Antworten ändern
					</button>
				</div>
			</section>
		</div>
	);
}
